import React from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, XCircle, TrendingUp } from 'lucide-react';
import { logger } from '../utils/logger';

const CombinedMeetingStatus = ({ meetings, userType }) => {
  // Helper function to parse date strings as local dates (avoiding timezone issues)
  const parseLocalDate = (dateString) => {
    if (dateString && dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      return new Date(dateString);
    }
  };

  // Get current date in user's local timezone
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  // Filter to only future meetings
  const futureMeetings = meetings.filter(meeting => {
    try {
      const meetingDate = parseLocalDate(meeting.date || meeting.meeting_date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate >= currentDate;
    } catch (error) {
      logger.warn('Error parsing meeting date:', meeting);
      return false;
    }
  });

  // Calculate status counts for all meetings (not just future)
  const allMeetingStatusCounts = meetings.reduce((counts, meeting) => {
    const status = (meeting.status || 'pending').toLowerCase();
    if (status === 'cancelled') {
      counts.cancelled++;
    }
    return counts;
  }, {
    cancelled: 0
  });

  // Calculate status counts for future meetings
  const statusCounts = futureMeetings.reduce((counts, meeting) => {
    const status = (meeting.status || 'pending').toLowerCase();

    // Map various status values to our main categories
    switch (status) {
      case 'confirmed':
      case 'approved':
        counts.confirmed++;
        break;
      case 'pending':
      case 'requested':
        counts.pending++;
        break;
      case 'declined':
      case 'rejected':
        counts.declined++;
        break;
      default:
        // Default unknown status to pending
        counts.pending++;
    }
    return counts;
  }, {
    pending: 0,
    confirmed: 0,
    declined: 0
  });

  const totalFuture = futureMeetings.length;
  const confirmedPercentage = totalFuture > 0 ? Math.round((statusCounts.confirmed / totalFuture) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Future Meetings</h3>
            <p className="text-sm text-gray-600">All upcoming meetings by status</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          <span className="text-2xl font-bold text-gray-900">{totalFuture}</span>
        </div>
      </div>

      {/* Total count with confirmation rate */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Future Meetings</p>
            <p className="text-2xl font-bold text-gray-900">{totalFuture}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Confirmed Rate</p>
            <p className={`text-xl font-semibold ${confirmedPercentage >= 70 ? 'text-green-600' : confirmedPercentage >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
              {confirmedPercentage}%
            </p>
          </div>
        </div>
      </div>

      {/* Status breakdown in 2x2 grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Confirmed */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Confirmed</span>
          </div>
          <div className="text-2xl font-bold text-green-800 mb-1">{statusCounts.confirmed}</div>
          <p className="text-sm text-green-600">Ready to go</p>
        </div>

        {/* Cancelled */}
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-800">Cancelled</span>
          </div>
          <div className="text-2xl font-bold text-red-800 mb-1">{allMeetingStatusCounts.cancelled}</div>
          <p className="text-sm text-red-600">Cancelled meetings</p>
        </div>

        {/* Pending */}
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">Pending</span>
          </div>
          <div className="text-2xl font-bold text-yellow-800 mb-1">{statusCounts.pending}</div>
          <p className="text-sm text-yellow-600">Awaiting response</p>
        </div>

        {/* Declined or placeholder */}
        {statusCounts.declined > 0 ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="font-medium text-red-800">Declined</span>
            </div>
            <div className="text-2xl font-bold text-red-800 mb-1">{statusCounts.declined}</div>
            <p className="text-sm text-red-600">Not happening</p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="w-5 h-5 text-gray-400" />
              <span className="font-medium text-gray-600">Declined</span>
            </div>
            <div className="text-2xl font-bold text-gray-600 mb-1">0</div>
            <p className="text-sm text-gray-500">None declined</p>
          </div>
        )}
      </div>

      {/* No meetings message */}
      {totalFuture === 0 && (
        <div className="text-center py-6">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No future meetings scheduled</p>
          <p className="text-sm text-gray-500 mt-1">
            {userType === 'sales' ? 'Start by finding healthcare providers to meet' : 'Meeting requests will appear here'}
          </p>
        </div>
      )}
    </div>
  );
};

export default CombinedMeetingStatus;